import Link from 'next/link';
import useLocale from 'next-translate/useTranslation';
import { memo, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ProductWithPrice, SubscriptionName } from 'types';

import Modal from '@/components/atoms/Modal';
import { SimpleTooltip } from '@/components/ui/tooltip';

import {
  CREDIT_PRICES,
  CREDIT_PURCHASE_DISCOUNT_PERCENT,
  getDiscountedCreditPrice,
} from '@/constants/common';
import { IMAGES_IN_PLAN, upgrade_product_ids } from '@/constants/common';
import { useAnalytics } from '@/contexts/Analytics';
import { postData } from '@/utils/helpers';
import { getStripe } from '@/utils/stripe-client';
import { createClient } from '@/utils/supabase/component';
import { useUser } from '@/utils/useUser';

import Button from '../buttons/Button';

interface CreditsDisplayProps {
  totalUsed: number;
  totalAvailable: number;
  bonusCredits?: number;
  subscriptionName: SubscriptionName;
  isFreePlan: boolean;
  type: 'images' | 'videos';
  showBuyButton?: boolean;
  /** Visual weight of the standalone Buy button (default 'outline'). */
  buttonVariant?: 'outline' | 'dark';
  /**
   * Open the credit-pack modal automatically (once) when a PAID user is out of
   * credits — the "pop up when I need more credits" customers ask for.
   */
  autoOpenWhenOut?: boolean;
}

function CreditsDisplay({
  totalUsed,
  totalAvailable,
  bonusCredits = 0,
  subscriptionName,
  isFreePlan,
  type,
  showBuyButton = true,
  buttonVariant = 'outline',
  autoOpenWhenOut = false,
}: CreditsDisplayProps) {
  const [showPricingModal, setShowPricingModal] = useState(false);
  const autoOpenedRef = useRef(false);
  const [products, setProducts] = useState<ProductWithPrice[]>([]);
  const [billingInterval, setBillingInterval] = useState<'month' | 'year'>(
    'year',
  );
  const analytics = useAnalytics();
  const { t } = useLocale('ai-images');
  const supabaseClient = createClient();
  const { subscription } = useUser();

  const remainingCredits = Math.max(0, totalAvailable - (totalUsed || 0));
  const isLowCredits = remainingCredits <= totalAvailable * 0.2; // Less than 20% remaining
  const isOutOfCredits = remainingCredits <= 0;

  useEffect(() => {
    if (!autoOpenWhenOut || isFreePlan || !isOutOfCredits) return;
    if (autoOpenedRef.current) return;
    autoOpenedRef.current = true;
    // Defer past hydration: opening the headlessui Dialog in the mount effect
    // renders it before its Transition is ready (collapsed, off-screen).
    const timer = window.setTimeout(() => setShowPricingModal(true), 400);
    return () => window.clearTimeout(timer);
  }, [autoOpenWhenOut, isFreePlan, isOutOfCredits]);

  useEffect(() => {
    if (showPricingModal) {
      getProductsWithPricesInArray(upgrade_product_ids)
        .then((data) => {
          setProducts(data as ProductWithPrice[]);
        })
        .catch((error) => {
          console.error('Error fetching products:', error);
        });
    }
  }, [showPricingModal]);

  // Filter products to show only next two tiers above current plan
  const getNextTierProducts = () => {
    if (!products.length || !subscription?.prices?.products?.name) return [];

    // Get the current product index in the ordered list
    const currentPlanName = subscription.prices.products.name;
    const productsList = products.map((p) => p.name);
    const currentPlanIndex = productsList.findIndex(
      (name) => name === currentPlanName,
    );

    // If plan not found or already at top tier, show the two top tiers
    if (
      currentPlanIndex === -1 ||
      currentPlanIndex >= productsList.length - 1
    ) {
      return products.slice(0, 2);
    }

    // Return next two tiers
    return products.slice(currentPlanIndex + 1, currentPlanIndex + 3);
  };

  const nextTierProducts = getNextTierProducts();

  const getProductsWithPricesInArray = async (product_ids: string[]) => {
    const { data, error } = await supabaseClient
      .from('products')
      .select('*, prices(*)')
      .in('id', product_ids)
      .eq('prices.active', true)
      .order('metadata->index')
      .order('unit_amount', { foreignTable: 'prices' });

    if (error) {
      console.log(error.message);
      throw error;
    }

    return data || [];
  };

  const handleStripeProduct = async (price: string) => {
    try {
      const { sessionId } = await postData({
        url: '/api/create-checkout-session-product',
        data: { price },
        token: '',
      });

      analytics.track(
        `${type.charAt(0).toUpperCase() + type.slice(1)} Purchase Initiated`,
        {
          price,
        },
      );

      const stripe = await getStripe();
      stripe?.redirectToCheckout({ sessionId });
    } catch (error) {
      return toast.error((error as Error)?.message);
    }
  };

  const handleSubscription = async (priceId: string) => {
    try {
      const { url } = await postData({
        url: '/api/create-portal-link',
        token: '',
      });

      analytics.track('Plan Upgrade Initiated', {
        priceId,
      });

      window.location.assign(url);
    } catch (error) {
      return toast.error((error as Error)?.message);
    }
  };

  // Calculate credits per plan for comparison
  const getCreditsPerPlan = (productName: string): number => {
    return IMAGES_IN_PLAN[productName as keyof typeof IMAGES_IN_PLAN] || 0;
  };

  // Format price with currency
  const formatPrice = (amount: number | null | undefined): string => {
    if (!amount) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(amount / 100);
  };

  // Format direct price without dividing by 100 (for already processed prices)
  const formatDirectPrice = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <>
      <Modal
        showModal={showPricingModal}
        onClose={() => setShowPricingModal(false)}
        className='sm:max-w-5xl'
      >
        <div className='px-4 pb-4 pt-5 sm:p-6 sm:pb-4'>
          <h3 className='mb-5 text-2xl font-bold'>{t('Purchase Options')}</h3>

          <div className='flex justify-center mb-6'>
            <div className='flex items-center bg-gray-100 dark:bg-slate-800 rounded-lg p-1'>
              <button
                onClick={() => setBillingInterval('month')}
                className={`py-2 px-4 text-sm font-medium rounded-md ${
                  billingInterval === 'month'
                    ? 'bg-white dark:bg-slate-700 shadow-sm text-primary-600 dark:text-primary-400'
                    : 'text-gray-500 dark:text-gray-400'
                }`}
              >
                {t('Monthly')}
              </button>
              <button
                onClick={() => setBillingInterval('year')}
                className={`py-2 px-4 text-sm font-medium rounded-md ${
                  billingInterval === 'year'
                    ? 'bg-white dark:bg-slate-700 shadow-sm text-primary-600 dark:text-primary-400'
                    : 'text-gray-500 dark:text-gray-400'
                }`}
              >
                {t('Yearly')}{' '}
                {billingInterval === 'year' && (
                  <span className='text-xs text-green-500 ml-1'>-50%</span>
                )}
              </button>
            </div>
          </div>

          {/* <div className='mb-4 bg-primary-50 p-3 rounded-lg border border-primary-100 text-center dark:bg-slate-800 dark:border-slate-700'>
            <p className='text-sm font-medium text-primary-700 dark:text-primary-400'>
              {t('Compare options to find the best value for your needs')}
            </p>
          </div> */}

          {/* Upgrade notice */}
          <div className='mb-5 p-3 rounded-lg border border-green-200 bg-green-50 dark:bg-green-900/20 dark:border-green-900/30'>
            <p className='text-sm font-medium text-green-700 dark:text-green-400'>
              <span className='font-bold'>Note:</span>{' '}
              {t(
                'When upgrading your current plan, you will only be charged the price difference. Your billing date will remain the same.',
              )}
            </p>
          </div>

          {/* Headers */}
          <div className='grid grid-cols-4 gap-4 mb-2'>
            <div className='col-span-1 text-xs uppercase text-gray-500 dark:text-gray-400 font-medium px-2'>
              {t('Option')}
            </div>
            <div className='col-span-1 text-xs uppercase text-gray-500 dark:text-gray-400 font-medium px-2 text-center'>
              {t('Price')}
            </div>
            <div className='col-span-1 text-xs uppercase text-gray-500 dark:text-gray-400 font-medium px-2 text-center'>
              {t('Credits')}
            </div>
            <div className='col-span-1 text-xs uppercase text-gray-500 dark:text-gray-400 font-medium px-2 text-center'>
              {t('Price per Credit')}
            </div>
          </div>

          {/* Subscription Options */}
          {nextTierProducts.length > 0 ? (
            nextTierProducts.map((product, index) => {
              const price = product?.prices?.find(
                (price) => price.interval === billingInterval,
              );
              if (!price) return null;

              const priceAmount = (price?.unit_amount || 0) / 100;
              const monthlyPrice =
                billingInterval === 'year' ? priceAmount / 12 : priceAmount;
              const creditsInPlan = getCreditsPerPlan(product.name as string);
              const pricePerCredit = creditsInPlan
                ? (monthlyPrice / creditsInPlan).toFixed(3)
                : 'N/A';

              // Mark the second plan or higher credit plan as best value
              const isBestValue =
                index === 1 || (index === 0 && nextTierProducts.length === 1);

              return (
                <div
                  key={product.id}
                  className='grid grid-cols-4 gap-4 mb-2 items-center p-3 rounded-lg border border-gray-200 hover:border-primary-500 hover:bg-primary-50 dark:border-white/5 dark:hover:bg-slate-800 cursor-pointer'
                  onClick={() => handleSubscription(price.id)}
                >
                  <div className='col-span-1'>
                    <div className='flex items-center'>
                      <h4 className='text-base font-semibold mr-2'>
                        {product.name}
                      </h4>
                      {isBestValue && (
                        <span className='rounded-full hidden md:block bg-green-100 px-2 py-0.5 text-xs text-green-800 dark:bg-green-900/50 dark:text-green-400'>
                          {t('Best value')}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className='col-span-1 text-center'>
                    <div className='text-lg font-bold text-primary-500'>
                      {formatPrice(price.unit_amount)}
                      <span className='text-xs font-normal'>
                        /{billingInterval === 'year' ? t('year') : t('month')}
                      </span>
                    </div>
                    {billingInterval === 'year' && (
                      <div className='text-xs text-green-500'>
                        {formatDirectPrice(monthlyPrice)}/{t('month')}
                      </div>
                    )}
                  </div>
                  <div className='col-span-1 text-center'>
                    <span className='font-medium'>
                      {creditsInPlan}/{t('month')}
                    </span>
                  </div>
                  <div className='col-span-1 text-center'>
                    <span className='font-medium'>${pricePerCredit}</span>
                    <div className='mt-1'>
                      <Button
                        variant='primary'
                        className='px-2 py-1 text-xs'
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSubscription(price.id);
                        }}
                      >
                        {t('Upgrade')}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className='text-center py-4 text-gray-500 dark:text-gray-400'>
              {t('No upgrade plans available at this time.')}
            </div>
          )}

          {/* Credit Options */}
          <div className='mt-6 mb-2'>
            <h4 className='font-semibold text-gray-500 dark:text-gray-400 text-sm'>
              {t('One-time Credit Purchases')}
            </h4>
          </div>

          {Object.values(CREDIT_PRICES)
            .slice(0, 2)
            .map((tier) => {
              const discountedPrice = getDiscountedCreditPrice(tier.price);
              return (
                <div
                  key={tier.credits}
                  className='grid grid-cols-4 gap-4 mb-2 items-center p-3 rounded-lg border border-gray-200 hover:border-primary-500 hover:bg-primary-50 dark:border-white/5 dark:hover:bg-slate-800 cursor-pointer'
                  onClick={() => handleStripeProduct(tier.priceId)}
                >
                  <div className='col-span-1'>
                    <div className='flex items-center'>
                      <h4 className='text-base font-semibold mr-2'>
                        {t('One-time Purchase')}
                      </h4>
                      <span className='rounded-full hidden md:block bg-green-100 px-2 py-0.5 text-xs text-green-800 dark:bg-green-900/50 dark:text-green-400'>
                        -{CREDIT_PURCHASE_DISCOUNT_PERCENT}%
                      </span>
                    </div>
                  </div>
                  <div className='col-span-1 text-center'>
                    <div className='text-lg font-bold text-primary-500'>
                      $
                      {Number.isInteger(discountedPrice)
                        ? discountedPrice
                        : discountedPrice.toFixed(2)}
                    </div>
                    <div className='text-xs text-gray-400 line-through'>
                      ${tier.price}
                    </div>
                  </div>
                  <div className='col-span-1 text-center'>
                    <span className='font-medium'>{tier.credits}</span>
                  </div>
                  <div className='col-span-1 text-center'>
                    <span className='font-medium'>
                      ${(discountedPrice / tier.credits).toFixed(3)}
                    </span>
                    <div className='mt-1'>
                      <Button
                        variant='outline'
                        className='px-2 py-1 text-xs'
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStripeProduct(tier.priceId);
                        }}
                      >
                        {t('Buy Now')}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}

          <div className='mt-4 text-center'>
            <p className='text-sm text-green-600 dark:text-green-400 font-semibold'>
              {t(
                'Subscribing to a plan is more cost-effective than buying individual credits!',
              )}
            </p>
          </div>
        </div>
      </Modal>

      {showBuyButton ? (
        <>
          {!isFreePlan && (
            <Button
              variant={buttonVariant}
              onClick={() => setShowPricingModal(true)}
              className={
                isLowCredits && buttonVariant === 'outline'
                  ? 'animate-pulse border-yellow-500 bg-yellow-50 text-yellow-700'
                  : ''
              }
            >
              {isOutOfCredits
                ? t('Out of credits - Buy more')
                : t('Buy more credits')}
            </Button>
          )}
        </>
      ) : (
        // Show credits information with link
        <div className='flex items-center'>
          <SimpleTooltip
            title={
              bonusCredits
                ? `You have ${remainingCredits} ${type} credits remaining out of ${totalAvailable} (+ ${bonusCredits} bonus credits) in your ${subscriptionName} plan.`
                : `You have ${remainingCredits} ${type} credits remaining out of ${totalAvailable} in your ${subscriptionName} plan.`
            }
          >
            <div
              className={`flex px-2 items-center gap-2 rounded-full py-1.5 ${
                isOutOfCredits
                  ? 'bg-red-50 text-red-700 dark:bg-red-900/50 dark:text-red-400'
                  : isLowCredits
                    ? 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-400'
                    : 'text-gray-500 dark:text-slate-400'
              }`}
            >
              <div className='relative flex h-4 w-4 items-center justify-center'>
                <svg className='h-4 w-4 -rotate-90 transform'>
                  <circle
                    className='text-gray-200 dark:text-slate-700'
                    strokeWidth='2.5'
                    stroke='currentColor'
                    fill='transparent'
                    r='7'
                    cx='8'
                    cy='8'
                  />
                  <circle
                    className={
                      isOutOfCredits
                        ? 'text-red-500'
                        : isLowCredits
                          ? 'text-yellow-500'
                          : 'text-primary-500'
                    }
                    strokeWidth='2.5'
                    strokeDasharray={44}
                    strokeDashoffset={
                      44 *
                      Math.max(
                        0,
                        Math.min(1, (totalUsed || 0) / totalAvailable),
                      )
                    }
                    strokeLinecap='round'
                    stroke='currentColor'
                    fill='transparent'
                    r='7'
                    cx='8'
                    cy='8'
                  />
                </svg>
              </div>
              <span className='text-sm font-semibold'>
                {remainingCredits} {t('credits')}
              </span>
              {!isFreePlan && isOutOfCredits && (
                <button
                  type='button'
                  onClick={() => setShowPricingModal(true)}
                  className={`ml-2 ${
                    isOutOfCredits || isLowCredits
                      ? 'text-yellow-600 hover:text-yellow-700 dark:text-yellow-400 dark:hover:text-yellow-300'
                      : 'text-primary-500 hover:text-primary-600 dark:text-primary-400 dark:hover:text-primary-300'
                  } text-xs hover:underline`}
                >
                  {t('Buy more credits')}
                </button>
              )}{' '}
              {isFreePlan && (
                <Link
                  href='/settings/billing'
                  className='rounded-md bg-primary-500 px-2 py-1 text-xs text-white'
                >
                  {t('Upgrade')}
                </Link>
              )}
            </div>
          </SimpleTooltip>
        </div>
      )}
    </>
  );
}

//memoized export
export default memo(CreditsDisplay);
